import React, { useState, useEffect } from "react";
import "../css/Header.css";
import profileImg from "../assets/profile_img.png";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";

import GiftModal from "./GiftModal";
import NotificationsModal from "./NotificationsModal";

import { getGifts, deleteGift, getNotificationsApi, markNotificationRead } from "../api";

// Helper function to format notification time
const formatNotificationTime = (dateString) => {
  if (!dateString) return "Just now";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const Header = () => {
  const [hasNotification, setHasNotification] = useState(false);
  const [isBellClicked, setIsBellClicked] = useState(false);
  const [isGiftClicked, setIsGiftClicked] = useState(false);

  const [isAddingGift, setIsAddingGift] = useState(false);
  const [isEditingGift, setIsEditingGift] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [newGift, setNewGift] = useState({ title: "", desc: "",couponCode:"" });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // ✅ Fetch gifts from backend
  useEffect(() => {
    const fetchGiftsData = async () => {
      try {
        setLoading(true);
        const { data } = await getGifts();
     

        // Map backend response to frontend structure
        const mapped = data.map((gift) => ({
          id: gift.id,
          title: gift.title,
          desc: gift.description,
          couponCode: gift.coupon_code,
          createdAt: gift.created_at,
          
        }));

        setGifts(mapped);
      } catch (err) {
        console.error("Failed to fetch gifts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGiftsData();
  }, []);

  // ✅ Fetch notifications from backend
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setNotificationsLoading(true);
        const { data } = await getNotificationsApi();
        
        // API response structure: { notifications: [...] }
        const notificationsArray = data?.notifications || [];
        
        // Map backend response to frontend structure
        const mapped = notificationsArray.map((notification) => ({
          id: notification.id,
          title: notification.title || "Notification",
          desc: notification.body || "",
          time: formatNotificationTime(notification.created_at),
          isRead: notification.is_read || false,
        }));
        
        setNotifications(mapped);
        
        // Show notification dot if there are unread notifications
        const hasUnread = mapped.some((n) => !n.isRead);
        setHasNotification(hasUnread);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
        console.error("Error details:", err.response?.data || err.message);
        // Keep empty array on error
        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  // Toggle Bell
  const handleBellClick = async () => {
    setIsBellClicked(!isBellClicked);
    setIsGiftClicked(false);
    
    // Refresh notifications when opening the modal
    if (!isBellClicked) {
      try {
        const { data } = await getNotificationsApi();
        const notificationsArray = data?.notifications || [];
        
        const mapped = notificationsArray.map((notification) => ({
          id: notification.id,
          title: notification.title || "Notification",
          desc: notification.body || "",
          time: formatNotificationTime(notification.created_at),
          isRead: notification.is_read || false,
        }));
        
        setNotifications(mapped);
        const hasUnread = mapped.some((n) => !n.isRead);
        setHasNotification(hasUnread);
      } catch (err) {
        console.error("Failed to refresh notifications:", err);
      }
    }
    
    setHasNotification(false);
  };

  // Toggle Gift
  const handleGiftClick = () => {
    setIsGiftClicked(!isGiftClicked);
    setIsBellClicked(false);
    setIsAddingGift(false);
    setIsEditingGift(false);
  };

  // Delete gift handler
  const handleDeleteGift = async () => {
    try {
      const giftToDelete = gifts[deleteIndex];
      if (giftToDelete?.id) {
        await deleteGift(giftToDelete.id);
      }
      setGifts(gifts.filter((_, i) => i !== deleteIndex));
    } catch (error) {
      console.error("Error deleting gift:", error);
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteIndex(null);
    }
  };

  // Handle notification click - mark as read and remove from list
  const handleNotificationClick = async (notificationId) => {
    try {
      // Call API to mark notification as read
      await markNotificationRead(notificationId);
      
      // Remove notification from the list and update notification dot
      setNotifications((prev) => {
        const remaining = prev.filter((n) => n.id !== notificationId);
        const hasUnread = remaining.some((n) => !n.isRead);
        setHasNotification(hasUnread);
        return remaining;
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  return (
    <header className="header">
      <div className="search-container"></div>

      <div className="profile-notification-container">
        {/* Gift Button */}
        <button
          className={`bell-button ${isGiftClicked ? "bell-clicked" : ""}`}
          aria-label="Gift"
          onClick={handleGiftClick}
        >
          <div className="bell-icon-wrapper">
            {/* 🎁 SVG */}
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
              <path
                fill="#F07AA3"
                d="M9.06 1.93C7.17 1.92 5.33 3.74 6.17 6H3a2 2 0 0 0-2 2v2a1 1 0 0 0 1 1h9V8h2v3h9a1 1 0 0 0 1-1V8a2 2 0 0 0-2-2h-3.17C19 2.73 14.6.42 12.57 3.24L12 4l-.57-.78c-.63-.89-1.5-1.28-2.37-1.29M9 4c.89 0 1.34 1.08.71 1.71S8 5.89 8 5a1 1 0 0 1 1-1m6 0c.89 0 1.34 1.08.71 1.71S14 5.89 14 5a1 1 0 0 1 1-1M2 12v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-8h-9v8h-2v-8z"
              />
            </svg>
          </div>
        </button>

        {/* Bell Button */}
        <button
          className={`bell-button ${isBellClicked ? "bell-clicked" : ""}`}
          aria-label="Notifications"
          onClick={handleBellClick}
        >
          <div className="bell-icon-wrapper">
            {/* 🔔 SVG */}
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 36 36">
              <path
                fill="#F07AA3"
                d="M32.51 27.83A14.4 14.4 0 0 1 30 24.9a12.6 12.6 0 0 1-1.35-4.81v-4.94A10.81 10.81 0 0 0 19.21 4.4V3.11a1.33 1.33 0 1 0-2.67 0v1.31a10.81 10.81 0 0 0-9.33 10.73v4.94a12.6 12.6 0 0 1-1.35 4.81a14.4 14.4 0 0 1-2.47 2.93a1 1 0 0 0-.34.75v1.36a1 1 0 0 0 1 1h27.8a1 1 0 0 0 1-1v-1.36a1 1 0 0 0-.34-.75"
              />
              <path
                fill="#F07AA3"
                d="M18 34.28A2.67 2.67 0 0 0 20.58 32h-5.26A2.67 2.67 0 0 0 18 34.28"
              />
            </svg>
            {hasNotification && !isBellClicked && (
              <span className="notification-dot" />
            )}
          </div>
        </button>

        <img src={profileImg} alt="Profile" className="profile-pic" />
      </div>

      {/* Gift Modal */}
      {isGiftClicked && (
        <GiftModal
          gifts={gifts}
          setGifts={setGifts}
          isAddingGift={isAddingGift}
          setIsAddingGift={setIsAddingGift}
          isEditingGift={isEditingGift}
          setIsEditingGift={setIsEditingGift}
          editIndex={editIndex}
          setEditIndex={setEditIndex}
          newGift={newGift}
          setNewGift={setNewGift}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setDeleteIndex={setDeleteIndex}
        />
      )}

      {/* Notifications Modal */}
      {isBellClicked && (
        <NotificationsModal 
          notifications={notifications} 
          onNotificationClick={handleNotificationClick}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteWithConfirmation
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteGift}
      />
    </header>
  );
};

export default Header;
